import { Component, computed, effect, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { ActivatedRoute, Router } from '@angular/router';
import { VoucherGridItemObject } from '@it2go/types';
import { VouchersPageStore } from './vouchers-page.store';
import { selectCurrentService } from '../../../store/person/person.selectors';
import {
    VoucherExportDialogComponent,
} from '../../../components/person/voucher-export-dialog-component/voucher-export-dialog.component';
import { isAllowed, routeParams } from '../../../../shared/shared.global-service';
import { FilterBuilder } from '../../../helper/filter.builder';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { contentGlobalSuccess } from '../../../content.error';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { VoucherCopyDialogComponent } from './voucher-copy-dialog/voucher-copy-dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { insuranceAbbrs } from '@libs/types/src/common/enum/insurance-abbr.enum';
import { VouchersPageStoreV2 } from './vouchers-page.storeV2';
import { vouchersFrag } from './vouchers-page.query';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { DynamicDialogRef } from '@openng/optimus-ui/dynamicdialog';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { ShortDatePipe } from '../../../../shared/pipes/short-date.pipe';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { doctorChipField } from '../../../../shared/components/kit/table/table.fields';

@Component({
    selector: 'app-vouchers-page',
    templateUrl: 'vouchers-page.component.html',
    styleUrls: ['vouchers-page.component.scss'],
    providers: [VouchersPageStore, VouchersPageStoreV2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        TableComponent,
        PrimeTemplate,
        Tooltip,
        TranslatePipe,
        ShortDatePipe,
    ],
})
export class VouchersPageComponent extends SubscriptionComponent {

    public readonly storeV2 = inject(VouchersPageStoreV2);
    public readonly confirm = inject(ConfirmBuilder);
    public readonly dialogService = inject(BaseDialogService);
    public readonly translate = inject(TranslateService);
    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly store = inject(VouchersPageStore);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);


    @ViewChild('voucherTable') voucherTable!: TableComponent;

    protected expertiseCode: string = '';
    protected columns = computed(() => {
        const columns = [];
        if (this.store.showNursingCare()) {
            columns.push({ name: 'nursingCare' });
        }

        columns.push(
            ...(this.folderStore.canEdit() ? [{ name: 'copy' }] : []),
            { name: 'print' },
            {
                name: 'sequenceNumber',
                dataKey: null,
                data: (voucher: VoucherGridItemObject) => {
                    if (!voucher.voucherPrev)
                        return { seqNumber: voucher.sequenceNumber };

                    const issueDate = new Date(voucher.issueDate);
                    issueDate.setHours(0, 0, 0, 0);

                    const prevEndDate = new Date(voucher.voucherPrev.voucherTo);
                    prevEndDate.setDate(prevEndDate.getDate() + 1);
                    prevEndDate.setHours(0, 0, 0, 0);

                    return {
                        seqNumber: voucher.sequenceNumber,
                        showWarning: issueDate.getTime() !== prevEndDate.getTime() ||
                            voucher.sequenceNumber !== voucher.voucherPrev.sequenceNumber + 1,
                    };
                },
                sortable: true,
            },
            {
                name: 'issueDate',
                sortable: true,
                filterable: {
                    type: 'date',
                },
            },
            {
                name: 'voucherTo',
                sortable: true,
                filterable: {
                    type: 'date',
                },
            },
            {
                name: 'insuranceAbbr',
                filterable: {
                    type: 'select',
                    selectOptions: () => insuranceAbbrs,
                },
            },
            { name: 'primaryDiagnosisCode', filterable: true },
            doctorChipField('doctor'),
        );

        return columns;
    });

    ref: DynamicDialogRef | undefined;

    protected serviceId = 0;

    protected additionalFilter = computed(() => [[{
        column: 'folderId',
        operator: 'EQ',
        value: [routeParams.folderId()],
    }]]);

    constructor() {
        super();
        effect(() => {
            this.storeV2.api.getVouchers(FilterBuilder.filter({
                folderId: routeParams.folderId(),
            }), vouchersFrag).then();
        });

        this.subs.push(
            this.store.globalStore!.select(selectCurrentService).subscribe((service) => {
                    this.expertiseCode = service?.expertise?.code || '';
                    this.serviceId = service?.id || 0;
                },
            ),
        );
    }

    showDetail(voucher: VoucherGridItemObject) {
        return this.router.navigate([`./${voucher.id}`], { relativeTo: this.route });
    }

    create(voucherType: string) {
        return this.router.navigate([`./create-${voucherType}`], { relativeTo: this.route });
    }

    navigateToPlan(voucher: VoucherGridItemObject) {
        return this.router.navigate([`./${voucher.id}/plan`], { relativeTo: this.route });
    }

    openExportDialog(voucher: VoucherGridItemObject) {
        this.ref = this.dialogService.open(VoucherExportDialogComponent, {
            data: {
                voucherId: voucher.id,
                serviceId: this.serviceId,
            },
        });
    }

    protected async copyVoucher(voucher: VoucherGridItemObject): Promise<void> {
        this.dialogService.open(
            VoucherCopyDialogComponent,
            {
                header: this.translate.instant('voucher.copyDialog.label'),
                styleClass: 'p-dialog-width-30',
                data: { voucherId: voucher.id },
                focusOnShow: false,
            },
        ).onClose.subscribe((_it) => {
            if (_it) {
                this.voucherTable?.reload();
            }
        });
    }

    protected async deleteVoucher(event: any): Promise<void> {
        this.confirm.confirmDelete(
            async () => {
                await this.storeV2.api.deleteVoucher({ id: event.item.id });
                contentGlobalSuccess.next('common.deleted');
                this.voucherTable?.reload();
            },
            event,
        );
    }

    protected readonly isAllowed = isAllowed;
    protected readonly vouchersFrag = vouchersFrag;
}
